import React, { useEffect, useState } from 'react';
import { FlaskConical, AlertTriangle } from 'lucide-react';
import api from '../../api';

// Lab results read in through the Upload Hub; abnormal values (flagged or outside the range) are highlighted
export const LabResultsTab = ({ patientId }) => {
  const [labs, setLabs] = useState(null);
  const [onlyAbn, setOnlyAbn] = useState(false);
  useEffect(() => { api.get(`/patients/${patientId}/lab-results`).then((r) => setLabs(r.data.labs)).catch(() => setLabs([])); }, [patientId]);
  if (!labs) return <p className="text-gray-500" data-testid="labs-loading">Loading lab results…</p>;
  if (!labs.length) return <p className="text-gray-500" data-testid="labs-empty">No lab results yet. Upload a lab report in Upload Hub &gt; Lab results.</p>;
  const abn = labs.reduce((t, l) => t + l.abnormalCount, 0);
  return (
    <div className="space-y-4" data-testid="labs-tab">
      <div className="flex flex-wrap items-center gap-3">
        <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-semibold ${abn ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}`} data-testid="labs-abnormal-count">
          {abn ? <AlertTriangle className="w-4 h-4" /> : <FlaskConical className="w-4 h-4" />}{abn} abnormal value{abn === 1 ? '' : 's'} across {labs.length} report{labs.length === 1 ? '' : 's'}</span>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={onlyAbn} onChange={(e) => setOnlyAbn(e.target.checked)} data-testid="labs-only-abnormal" />Show abnormal only</label>
      </div>
      {labs.map((l) => {
        const rows = l.results.filter((x) => !onlyAbn || x.abnormal);
        if (!rows.length) return null;
        return (
          <div key={l.id} className="border rounded-lg overflow-hidden" data-testid={`lab-report-${l.id}`}>
            <div className="bg-gray-50 px-3 py-2 text-sm flex flex-wrap gap-x-5">
              <strong>{l.collectedDate || 'Date not listed'}</strong><span>{l.labName || 'Lab'}</span>
              {l.orderingPhysician && <span>Ordered by {l.orderingPhysician}</span>}
              {l.abnormalCount > 0 && <span className="text-red-700 font-semibold">{l.abnormalCount} abnormal</span>}
            </div>
            <table className="w-full text-sm">
              <thead className="text-left text-gray-500"><tr>{['Test', 'Result', 'Unit', 'Reference range', 'Flag'].map((h) => <th key={h} className="px-3 py-1.5">{h}</th>)}</tr></thead>
              <tbody>{rows.map((x, i) => (
                <tr key={i} className={`border-t ${x.abnormal ? 'bg-red-50 text-red-900 font-semibold' : ''}`} data-testid={`lab-row-${l.id}-${i}`}>
                  <td className="px-3 py-1.5">{x.test}</td><td className="px-3 py-1.5">{x.value}{x.abnormal && <AlertTriangle className="inline w-3.5 h-3.5 ml-1 text-red-600" />}</td>
                  <td className="px-3 py-1.5">{x.unit}</td><td className="px-3 py-1.5">{x.range}</td><td className="px-3 py-1.5">{x.flag && x.flag !== 'normal' ? x.flag : ''}</td>
                </tr>))}</tbody>
            </table>
            {l.notes && <p className="px-3 py-2 text-xs text-gray-600 border-t">{l.notes}</p>}
          </div>
        );
      })}
    </div>
  );
};
